import { useEffect, useMemo, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';

import type { EntityOption, EntitySource } from './types';

export type LoadEntityOptions = (search: string, unresolvedValues: string[]) => Promise<EntityOption[]>;

/**
 * Transforme une source en la fonction que consomment déjà les filtres de DataTable :
 * `loadOptions(search, unresolvedValues)`.
 *
 * Elle est **stable par construction** : le client Apollo l'est à vie et une source est une constante
 * de module. Plus de `useCallback` à entretenir par page — c'était le piège documenté dans
 * `TotemOrganizationSelect`, où une dépendance recréée à chaque rendu annulait le timer de debounce
 * du select avant qu'il ne se déclenche.
 */
export function useEntityOptions(source: EntitySource): LoadEntityOptions {
    const client = useApolloClient();

    return useMemo(() => {
        if (!source.preload) {
            return async (search, unresolvedValues) => {
                const { options } = await source.fetchOptions(client, { search, ids: unresolvedValues });

                return options;
            };
        }

        // Référentiel préchargé : UNE requête, puis filtrage local. Sans ce chemin, chaque frappe
        // repartirait au serveur pour filtrer une liste déjà entièrement chargée — et les
        // identifiants non résolus n'ont plus de sens, puisque tout est là.
        let loadedOptions: Promise<EntityOption[]> | null = null;

        return async (search) => {
            loadedOptions ??= source.fetchOptions(client, { search: '', ids: [] }).then(({ options }) => options);
            const options = await loadedOptions;
            const needle = search.trim().toLowerCase();

            return needle ? options.filter(({ label }) => label.toLowerCase().includes(needle)) : options;
        };
    }, [client, source]);
}

/**
 * Une source liée à un périmètre qui n'est connu qu'au rendu — les sous-catégories d'UNE catégorie,
 * les emplacements d'UNE colonne d'entrepôt.
 *
 * `withScope` rend un nouvel objet : l'appeler dans le corps d'un composant produirait une source
 * différente à chaque rendu, et relancerait sans fin l'effet de recherche qui la met en dépendance.
 * La mémoïsation se fait donc sur la valeur SÉRIALISÉE du périmètre, pas sur son identité.
 */
export function useScopedEntitySource<TScope>(
    source: EntitySource & { withScope: (scope: TScope) => EntitySource },
    scope: TScope,
): EntitySource {
    const scopeKey = JSON.stringify(scope ?? null);

    const scopeRef = useRef(scope);
    scopeRef.current = scope;

    // La dépendance est `scopeKey`, PAS `scope` : c'est tout l'objet du hook. La valeur courante est
    // lue dans une ref, comme le fait déjà `useDataTableFilters` pour ses `pinnedFilters`.
    return useMemo(() => source.withScope(scopeRef.current), [source, scopeKey]);
}

/**
 * Les options à afficher tant que l'utilisateur n'a rien tapé : la première page du référentiel, ET
 * les valeurs déjà sélectionnées, nommées.
 *
 * Un seul aller-retour rend les deux — c'est précisément ce que fait `findOptions` côté serveur, dont
 * la branche `ids` est séparée de la branche de recherche. Sans ça, une valeur venue d'une URL ou d'un
 * formulaire s'afficherait en identifiant brut : c'est le défaut actuel du mode multi de
 * `TotemSelect`, qui rend « Introuvable (…) ».
 */
export function useEntityDefaultOptions(
    loadOptions: LoadEntityOptions,
    selectedValues: string[],
): { defaultOptions: EntityOption[]; isLoading: boolean } {
    const [knownOptions, setKnownOptions] = useState<Record<string, EntityOption>>({});
    const [pageOptions, setPageOptions] = useState<EntityOption[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    // Une réponse plus ancienne peut arriver après une plus récente : on ignore les périmées.
    const latestRequestRef = useRef(0);
    // Ce pour quoi la première page a déjà été chargée. Sans ce garde-fou, résoudre une sélection
    // viderait `unresolvedKey` et relancerait un second appel, pour rien.
    const loadedForRef = useRef<LoadEntityOptions | null>(null);

    const unresolvedKey = selectedValues.filter((value) => !knownOptions[value]).join(',');

    useEffect(() => {
        if (!unresolvedKey && loadedForRef.current === loadOptions) {
            return;
        }

        const requestId = ++latestRequestRef.current;
        loadedForRef.current = loadOptions;
        setIsLoading(true);

        void loadOptions('', unresolvedKey ? unresolvedKey.split(',') : [])
            .then((options) => {
                if (requestId !== latestRequestRef.current) {
                    return;
                }
                setPageOptions(options);
                setKnownOptions((previous) => ({
                    ...previous,
                    ...Object.fromEntries(options.map((option) => [option.value, option])),
                }));
            })
            .finally(() => {
                if (requestId === latestRequestRef.current) {
                    setIsLoading(false);
                }
            });
    }, [loadOptions, unresolvedKey]);

    const defaultOptions = useMemo(() => {
        const selectedOptions = selectedValues.map((value) => knownOptions[value] ?? { value, label: value });
        const remainingOptions = pageOptions.filter((option) => !selectedValues.includes(option.value));

        return [...selectedOptions, ...remainingOptions];
    }, [selectedValues, knownOptions, pageOptions]);

    return { defaultOptions, isLoading };
}
